import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Check, Loader2, Users, Calendar, Infinity as InfinityIcon } from "lucide-react";
import { toast } from "sonner";
import logo from "@/assets/logo-white.png";
import { useNativelyEnvironment } from "@/hooks/useNativelyEnvironment";

type PlanTier = "lite" | "pro" | "studio";

export default function ChooseTrial() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { isNativelyApp, isReady } = useNativelyEnvironment();
  const [selectedTier, setSelectedTier] = useState<PlanTier | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);

  // Redirect native app users to crew login
  useEffect(() => {
    if (isReady && isNativelyApp) {
      navigate("/crew-login", { replace: true });
    }
  }, [isReady, isNativelyApp, navigate]);

  useEffect(() => {
    const checkAuth = async () => {
      const {
        data: { session },
      } = await supabase.auth.getSession();

      if (!session) {
        navigate("/producer-login");
        return;
      }

      // Check if user already has a non-default tier set (meaning they already chose)
      const { data: subscription } = await supabase
        .from("producer_subscriptions")
        .select("plan_tier, plan_status, trial_ends_at")
        .eq("user_id", session.user.id)
        .single();

      // If user already has an active paid subscription, redirect to dashboard
      if (subscription?.plan_status === "active") {
        navigate("/dashboard");
        return;
      }

      // If user has expired status AND no active trial, redirect to dashboard
      if (
        subscription?.plan_status === "expired" &&
        (!subscription?.trial_ends_at || new Date(subscription.trial_ends_at) < new Date())
      ) {
        navigate("/dashboard");
        return;
      }

      setIsCheckingAuth(false);
    };

    checkAuth();
  }, [navigate]);

  const handleStartTrial = async () => {
    if (!selectedTier) return;
    setIsLoading(true);

    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();

      if (!session) {
        toast.error("Please log in first");
        navigate("/producer-login");
        return;
      }

      // Update the user's subscription tier
      const { error } = await supabase
        .from("producer_subscriptions")
        .update({ plan_tier: selectedTier })
        .eq("user_id", session.user.id);

      if (error) throw error;

      toast.success(`Your ${selectedTier.toUpperCase()} trial has started!`);
      // Invalidate cached subscription/session data so dashboard shows correct tier
      await queryClient.invalidateQueries({ queryKey: ["subscription-status"] });
      await queryClient.invalidateQueries({ queryKey: ["user-session"] });
      navigate("/dashboard");
    } catch (error) {
      console.error(error);
      toast.error("Failed to start trial. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  if (isCheckingAuth) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Gradient background */}
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-primary/10 via-transparent to-secondary/10" />

      <div className="relative z-10 flex min-h-screen flex-col items-center justify-center p-6">
        {/* Logo */}
        <img src={logo} alt="PROduce Logo" className="mb-8 h-12" />

        {/* Header */}
        <div className="mb-10 text-center">
          <h1 className="mb-3 text-3xl font-bold md:text-4xl">
            Try PROduce free for <span className="text-primary">7 days</span>
          </h1>
          <p className="text-lg text-muted-foreground">Choose the plan that fits your needs</p>
        </div>

        {/* Plan Cards */}
        <div className="mb-10 grid w-full max-w-4xl gap-6 md:grid-cols-3">
          {/* LITE */}
          <Card
            className={`cursor-pointer border-2 bg-card/50 p-6 backdrop-blur-xl transition-all hover:scale-[1.02] ${
              selectedTier === "lite"
                ? "border-primary ring-2 ring-primary/20"
                : "border-border hover:border-primary/50"
            }`}
            onClick={() => setSelectedTier("lite")}
          >
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-xl font-bold">LITE</h3>
              {selectedTier === "lite" && (
                <div className="flex h-6 w-6 items-center justify-center rounded-full bg-primary">
                  <Check className="h-4 w-4 text-primary-foreground" />
                </div>
              )}
            </div>
            <p className="mb-6 text-muted-foreground">For simple productions</p>
            <ul className="space-y-3 text-sm">
              <li className="flex items-center gap-2">
                <Users className="h-4 w-4 text-primary" />
                <span>
                  <strong>1</strong> crew member per production
                </span>
              </li>
              <li className="flex items-center gap-2">
                <InfinityIcon className="h-4 w-4 text-primary" />
                <span>Unlimited productions</span>
              </li>
              <li className="flex items-center gap-2">
                <Check className="h-4 w-4 text-primary" />
                <span>AI-powered scheduling</span>
              </li>
              <li className="flex items-center gap-2">
                <Check className="h-4 w-4 text-primary" />
                <span>Real-time app updates</span>
              </li>
            </ul>
          </Card>

          {/* PRO */}
          <Card
            className={`relative cursor-pointer border-2 bg-card/50 p-6 backdrop-blur-xl transition-all hover:scale-[1.02] ${
              selectedTier === "pro"
                ? "border-primary ring-2 ring-primary/20"
                : "border-border hover:border-primary/50"
            }`}
            onClick={() => setSelectedTier("pro")}
          >
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-xl font-bold">PRO</h3>
              {selectedTier === "pro" && (
                <div className="flex h-6 w-6 items-center justify-center rounded-full bg-primary">
                  <Check className="h-4 w-4 text-primary-foreground" />
                </div>
              )}
            </div>
            <p className="mb-6 text-muted-foreground">
              For multi-day productions & full crew collaboration
            </p>
            <ul className="space-y-3 text-sm">
              <li className="flex items-center gap-2">
                <Users className="h-4 w-4 text-primary" />
                <span>
                  <strong>10</strong> crew members per production
                </span>
              </li>
              <li className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-primary" />
                <span>
                  <strong>10</strong> productions per year
                </span>
              </li>
              <li className="flex items-center gap-2">
                <Check className="h-4 w-4 text-primary" />
                <span>AI-powered scheduling</span>
              </li>
              <li className="flex items-center gap-2">
                <Check className="h-4 w-4 text-primary" />
                <span>Real-time app updates</span>
              </li>
              <li className="flex items-center gap-2">
                <Check className="h-4 w-4 text-primary" />
                <span>Live crew tracking</span>
              </li>
            </ul>
          </Card>

          {/* STUDIO */}
          <Card
            className={`cursor-pointer border-2 bg-card/50 p-6 backdrop-blur-xl transition-all hover:scale-[1.02] ${
              selectedTier === "studio"
                ? "border-secondary ring-2 ring-secondary/20"
                : "border-border hover:border-secondary/50"
            }`}
            onClick={() => setSelectedTier("studio")}
          >
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-xl font-bold">STUDIO</h3>
              {selectedTier === "studio" && (
                <div className="flex h-6 w-6 items-center justify-center rounded-full bg-secondary">
                  <Check className="h-4 w-4 text-secondary-foreground" />
                </div>
              )}
            </div>
            <p className="mb-6 text-muted-foreground">Unlimited productions, full power</p>
            <ul className="space-y-3 text-sm">
              <li className="flex items-center gap-2">
                <InfinityIcon className="h-4 w-4 text-secondary" />
                <span>
                  <strong>Unlimited</strong> crew members
                </span>
              </li>
              <li className="flex items-center gap-2">
                <InfinityIcon className="h-4 w-4 text-secondary" />
                <span>
                  <strong>Unlimited</strong> productions
                </span>
              </li>
              <li className="flex items-center gap-2">
                <Check className="h-4 w-4 text-secondary" />
                <span>All PRO features</span>
              </li>
              <li className="flex items-center gap-2">
                <Check className="h-4 w-4 text-secondary" />
                <span>Priority support</span>
              </li>
            </ul>
          </Card>
        </div>

        {/* CTA Button */}
        <Button
          onClick={handleStartTrial}
          variant="gradient"
          size="lg"
          className="px-12 py-6 text-lg"
          disabled={isLoading || !selectedTier}
        >
          {isLoading ? (
            <>
              <Loader2 className="mr-2 h-5 w-5 animate-spin" />
              Starting trial...
            </>
          ) : !selectedTier ? (
            "Select a plan to continue"
          ) : (
            "Start my 7-day trial"
          )}
        </Button>

        <p className="mt-4 text-sm text-muted-foreground">
          No credit card required • Cancel anytime
        </p>
      </div>
    </div>
  );
}
